import { MutableRefObject, ComponentProps, useRef } from 'react';

import { profileApi, ProfileModel } from '@pukka/api';
import { Status } from '@pukka/common/types';
import { assertUtil } from '@pukka/common/utils';
import { Button } from '@pukka/ui-components';
import { proxy } from '@pukka/valtio';
import { RefetchOptions } from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import noop from 'lodash/noop';
import { MatchSorterOptions } from 'match-sorter';

interface OutletSelectorState {
  isDataLoaded: boolean;
  status: Status;
  outlets: Set<ProfileModel.Outlet>;
  buttonProps: ComponentProps<typeof Button>;
  filterOptions: MatchSorterOptions<ProfileModel.Outlet>;
  onRetry: () => void;
  readonly useProfile: () => void;
}

function setOutletSelectorStateOnLoaded(
  state: OutletSelectorState,
  data: ProfileModel.Response | undefined,
  status: OutletSelectorState['status'],
  refetch: (opt?: RefetchOptions) => void,
) {
  if (!state.isDataLoaded && status === 'success') {
    state.outlets = new Set((data?.outlets ?? []).filter((p) => !isNil(p.name) && isNumber(p.id)) as Required<ProfileModel.Outlet>[]);
    state.isDataLoaded = true;
    state.status = status;
    state.onRetry = noop;
  } else if (status === 'error') {
    state.status = status;

    if (state.onRetry === noop) {
      state.onRetry = () => {
        state.status = 'loading';
        refetch();
      };
    }
  }
}

function createOutletSelectorProxy(buttonTitle?: string) {
  const State: OutletSelectorState = proxy({
    isDataLoaded: false,
    status: 'loading',
    outlets: new Set(),
    buttonProps: { children: buttonTitle ?? 'Assign Outlets' },
    filterOptions: { keys: ['name'] },
    useProfile() {
      const { data, status, refetch } = profileApi.useProfile({
        retry: 3,
        retryDelay: 500,
      });

      setOutletSelectorStateOnLoaded(State, data, status, refetch);
    },
    onRetry: noop,
  } satisfies OutletSelectorState);

  return State;
}

function createOutletSelectorState(ref: MutableRefObject<OutletSelectorState | null>, buttonTitle?: string) {
  if (ref.current === null) {
    ref.current = createOutletSelectorProxy(buttonTitle);
  }

  return ref.current;
}

function useOutletSelectorState(buttonTitle?: string) {
  const state = useRef<OutletSelectorState>(null);

  createOutletSelectorState(state, buttonTitle);
  assertUtil.assertRefIsNotNull(state);

  return state.current;
}

export type { OutletSelectorState };

export {
  useOutletSelectorState,
  createOutletSelectorState as _createOutletSelectorState,
  setOutletSelectorStateOnLoaded as _setOutletSelectorStateOnLoaded,
  createOutletSelectorProxy as _createOutletSelectorProxy,
};
